import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Paper, Typography } from "@mui/material";
import { useDispatch, useSelector } from 'react-redux';
import { CalendarDate } from "@internationalized/date";
import PageContainer from "../../common_components/PageContainer";
import PagesCommonHeader from "../../common_components/PagesCommonHeader";
import LastUpdatedInfo from "../../common_components/LastUpdatedInfo";
import { MonthPicker, Dropdown } from "../../common_components/FormInputs";
import TableToolbar from "../../common_components/TableToolbar";
import GoalExpandedContent from './components/GoalExpandedContent';
import GoalActionDrawer from './components/GoalActionDrawer';
import AppButton from '../../common_components/AppButton';
import { fetchDesignationWiseUsers, selectDesignationWiseUsers } from '../../redux/clientDataSlice';
import { AM_DESIGNATIONS, collectUsersByDesignations, toDesignationList } from '../../utils/designationFilters';
import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { selectUser, selectPermissions } from '../../redux/authSlice';
import { fetchGoals, syncGoals, setYearMonth, setGoalType, setPage, setSortConfig, setServiceType, selectGoals, selectGoalsLoading, selectGoalsSyncing, selectYearMonth, selectGoalType, selectPage, selectLimit, selectTotalGoals, selectLastUpdated, selectSortBy, selectSortOrder, selectServiceType, selectSearchTerm, approvePendingGoals, setSearchTerm } from '../../redux/goalSlice';
import CommonTable from '../../common_components/CommonTable';
import CommonTabs from '../../common_components/CommonTabs';
import Pagination from '../../common_components/Pagination';
import { useGoalTableConfigs } from './GoalManagementCells';
import CommonLoader from '../../common_components/CommonLoader';
import { useLayout } from "../../contexts/LayoutContext";
const GoalManagementMain = () => {
    const dispatch = useDispatch();
    const { setIsSidebarExpanded } = useLayout();
    useEffect(() => {
        setIsSidebarExpanded(false);
    }, [setIsSidebarExpanded]);
    const goals = useSelector(selectGoals);
    const loading = useSelector(selectGoalsLoading);
    const syncing = useSelector(selectGoalsSyncing);
    const year_month = useSelector(selectYearMonth);
    const goal_type = useSelector(selectGoalType);
    const page = useSelector(selectPage);
    const limit = useSelector(selectLimit);
    const totalEntries = useSelector(selectTotalGoals);
    const lastUpdated = useSelector(selectLastUpdated);
    const sort_by = useSelector(selectSortBy);
    const sort_order = useSelector(selectSortOrder);
    const service_type = useSelector(selectServiceType);
    const search_term = useSelector(selectSearchTerm);
    const designationWiseUsers = useSelector(selectDesignationWiseUsers);
    const [expandedRowId, setExpandedRowId] = useState(null);
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [selectedAM, setSelectedAM] = useState('');
    const [isRefreshModalOpen, setIsRefreshModalOpen] = useState(false);
    const [isApproving, setIsApproving] = useState(false);
    // Debounce search input so it doesn't trigger a fetchGoals API call on every keystroke.
    const searchDebounceRef = useRef(null);
    useEffect(() => {
        return () => {
            if (searchDebounceRef.current)
                clearTimeout(searchDebounceRef.current);
        };
    }, []);
    const handleSearchChange = (val) => {
        if (searchDebounceRef.current)
            clearTimeout(searchDebounceRef.current);
        if (val === '') {
            // Clearing the search (e.g. the "X" button) should reflect immediately, not wait out the debounce.
            dispatch(setSearchTerm(val));
            return;
        }
        searchDebounceRef.current = setTimeout(() => {
            dispatch(setSearchTerm(val));
        }, 400);
    };
    const user = useSelector(selectUser);
    const selectPermission = useSelector(selectPermissions);
    const isAM = toDesignationList(user?.user_type).includes('Account Managers');
    // const isAdmin = user?.role_id?.name === 'admin' || user?.role_id?.name === 'super_admin' || user?.role_id?.name === 'Master Admin';
    const goalActions = selectPermission?.client_goal?.actions || [];
    const showAMProjection = isAM || goalActions.includes('Manage AM Projection');
    const showProjectionGoal = goalActions.includes('View Projection Goals');
    const showCommissionGoal = goalActions.includes('View Commission Goals');
    useEffect(() => {
        if (isAM && user?.id) {
            setSelectedAM(String(user.id));
        }
    }, [isAM, user]);
    useEffect(() => {
        if (!showAMProjection && goal_type === 'am_projection') {
            if (showProjectionGoal)
                dispatch(setGoalType('projection_goal'));
            else if (showCommissionGoal)
                dispatch(setGoalType('commission_goal'));
        }
        else if (!showProjectionGoal && goal_type === 'projection_goal') {
            if (showAMProjection)
                dispatch(setGoalType('am_projection'));
            else if (showCommissionGoal)
                dispatch(setGoalType('commission_goal'));
        }
        else if (!showCommissionGoal && goal_type === 'commission_goal') {
            if (showProjectionGoal)
                dispatch(setGoalType('projection_goal'));
            else if (showAMProjection)
                dispatch(setGoalType('am_projection'));
        }
    }, [showAMProjection, showProjectionGoal, showCommissionGoal, goal_type, dispatch]);
    const accountManagers = useMemo(() => collectUsersByDesignations(designationWiseUsers?.designationMap, AM_DESIGNATIONS), [designationWiseUsers]);
    const amOptions = useMemo(() => [
        { value: '', label: 'All' },
        ...accountManagers.map((u) => ({
            value: u._id,
            label: `${u.first_name} ${u.last_name}`,
        })),
    ], [accountManagers]);
    const amDisplayValue = useMemo(() => {
        const found = accountManagers.find((u) => u._id === selectedAM);
        const label = found ? `${found.first_name} ${found.last_name}` : 'All';
        return (_jsxs("span", { children: [_jsx("span", { style: { color: '#101828', fontWeight: 500 }, children: "AM :" }), ' ', _jsx("span", { style: { color: '#667085' }, children: label })] }));
    }, [selectedAM, accountManagers]);
    const typeOptions = useMemo(() => [
        { value: 'paid_media', label: 'Paid Media' },
        { value: 'affiliate', label: 'Affiliate' },
    ], []);
    const typeDisplayValue = useMemo(() => {
        const selected = service_type ? service_type.split(',') : [];
        if (selected.length === 0 || selected.includes('')) {
            return (_jsxs("span", { children: [_jsx("span", { style: { color: '#101828', fontWeight: 500 }, children: "Type :" }), ' ', _jsx("span", { style: { color: '#667085' }, children: "All" })] }));
        }
        const labels = selected.map(val => typeOptions.find(t => t.value === val)?.label).filter(Boolean);
        let labelStr = 'All';
        if (labels.length === 1)
            labelStr = labels[0] || 'All';
        else if (labels.length === 2)
            labelStr = labels.join(', ');
        else if (labels.length > 2)
            labelStr = `${labels[0]}, ${labels[1]} +${labels.length - 2}`;
        return (_jsxs("span", { children: [_jsx("span", { style: { color: '#101828', fontWeight: 500 }, children: "Type :" }), ' ', _jsx("span", { style: { color: '#667085' }, children: labelStr })] }));
    }, [service_type, typeOptions]);
    useEffect(() => {
        dispatch(fetchDesignationWiseUsers());
    }, [dispatch]);
    const selectedCalendarDate = useMemo(() => {
        if (!year_month)
            return null;
        const [y, m] = year_month.split("-").map(Number);
        return new CalendarDate(y, m, 1);
    }, [year_month]);
    useEffect(() => {
        const promise = dispatch(fetchGoals({ year_month, goal_type, page, limit, am_id: selectedAM, sort_by, sort_order, service_type, search_term }));
        return () => {
            promise.abort();
        };
    }, [dispatch, year_month, goal_type, page, limit, selectedAM, sort_by, sort_order, service_type, search_term]);
    useEffect(() => {
        setExpandedRowId(null);
        setSelectedRows(new Set());
        dispatch(setSortConfig({ sort_by: null, sort_order: null }));
    }, [year_month, goal_type]);
    const handleMonthChange = (v) => {
        if (v !== "ALL_TIME") {
            const monthStr = `${v.year}-${String(v.month).padStart(2, "0")}`;
            dispatch(setYearMonth(monthStr));
        }
    };
    const handleRefresh = () => {
        setIsRefreshModalOpen(true);
    };
    const executeSync = (notifyAll = false) => {
        dispatch(syncGoals({ month: year_month, notify: notifyAll }));
        setIsRefreshModalOpen(false);
    };
    const handlePageChange = (newPage) => {
        dispatch(setPage(newPage));
    };
    const handleExpandToggle = (rowId) => {
        setExpandedRowId(prev => (prev === rowId ? null : rowId));
    };
    const sortConfig = sort_by && sort_order ? { columnId: sort_by, direction: sort_order } : null;
    const handleSortChange = (columnId, direction) => {
        dispatch(setSortConfig({ sort_by: direction ? columnId : null, sort_order: direction }));
    };
    const rowsWithId = useMemo(() => {
        return goals.map((row, idx) => ({
            ...row,
            id: row.summary?._id || `row-${idx}`
        }));
    }, [goals]);
    const hasWaitingApprovalGoal = useMemo(() => {
        return goals.some((row) => {
            const summary = row.summary || {};
            const types = summary.type || [];
            return types.some((t) => {
                const status = t === 'paid_media' ? summary.paid_approval_status : summary.aff_approval_status;
                return status === 'waiting_for_approval';
            });
        });
    }, [goals]);
    const prevYear = useMemo(() => {
        if (!year_month)
            return new Date().getFullYear() - 1;
        const year = parseInt(year_month.split('-')[0]);
        return year - 1;
    }, [year_month]);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerData, setDrawerData] = useState({
        client: null,
        action_type: 'plan',
        service_type: 'paid_media',
        month_label: ''
    });
    const handleActionClick = (data) => {
        const today = new Date();
        const currentYearMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
        const isPastMonth = year_month < currentYearMonth;
        const isReadOnly = isPastMonth && data.client?.status === 'inactive';
        setDrawerData({ ...data, isReadOnly });
        setDrawerOpen(true);
    };
    const { columns, renderCell } = useGoalTableConfigs({
        goal_type,
        prevYear,
        year_month,
        onActionClick: handleActionClick
    });
    const renderExpandedContent = (row) => (_jsx(GoalExpandedContent, { row: row, goalType: goal_type, onActionClick: handleActionClick }));
    const availableTabs = [
        { value: "am_projection", label: "AM Projection", show: showAMProjection },
        { value: "projection_goal", label: "Projection Goal", show: showProjectionGoal },
        { value: "commission_goal", label: "Commission Goal", show: showCommissionGoal }
    ].filter(t => t.show).map(t => ({ value: t.value, label: t.label }));
    const tabsSection = availableTabs.length > 0 ? (_jsx(CommonTabs, { selectedValue: goal_type, onChange: (val) => dispatch(setGoalType(val)), tabs: availableTabs })) : (_jsx(Typography, { sx: { fontWeight: 600, color: '#101828', fontSize: '16px', py: 1, px: 0.5 }, children: "Client Goals" }));
    const handleApprovedGoalClick = async () => {
        const client_ids = Array.from(selectedRows).map(id => {
            const row = rowsWithId.find(r => r.id === id);
            return row?.summary?.client_id;
        }).filter(Boolean);
        setIsApproving(true);
        try {
            await dispatch(approvePendingGoals({
                year_month,
                client_ids: client_ids.length > 0 ? client_ids : undefined
            })).unwrap();
            setSelectedRows(new Set());
        }
        finally {
            setIsApproving(false);
        }
    };
    return (_jsxs(PageContainer, { children: [_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '8px' }, children: [_jsx(PagesCommonHeader, { height: "65px", lastUpdatedSection: _jsx(Box, { sx: { display: 'flex', alignItems: 'center', gap: 2 }, children: _jsx(LastUpdatedInfo, { updatedAt: lastUpdated, onRefresh: handleRefresh, disabled: syncing, moduleName: "goals", showIcon: true }) }), rightSection: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(MonthPicker, { value: selectedCalendarDate, onChange: handleMonthChange, showAllTime: false, fullWidth: false, className: "header-dropdown", futureMonths: "ALL" }), _jsx(TableToolbar, { title: "", showSearch: true, showRows: false, showExpand: false, showFullScreen: true, searchValue: search_term, onSearchChange: handleSearchChange, searchPlaceholder: "Search clients..." })] }) }), _jsx(Paper, { elevation: 0, sx: { p: '8px', border: '1px solid #E4E7EC', overflow: 'hidden' }, children: _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: [_jsxs(Box, { sx: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }, children: [_jsx(Box, { sx: {
                                                display: 'flex',
                                                justifyContent: 'flex-start',
                                                pointerEvents: syncing ? 'none' : 'auto',
                                                opacity: syncing ? 0.6 : 1,
                                            }, children: tabsSection }), _jsxs(Box, { className: 'goal', sx: { display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1, flex: 1 }, children: [_jsx(Dropdown, { value: selectedAM, onChange: (val) => {
                                                        setSelectedAM(val);
                                                        dispatch(setPage(1));
                                                    }, options: amOptions, placeholder: 'AM: All', displayValue: amDisplayValue, leftIcon: '/assets/LeadAmC.svg', size: 'sm', fullWidth: false, className: 'header-dropdown', searchable: accountManagers.length > 5, usePortal: true, disabled: isAM || syncing }), _jsx(Dropdown, { multiple: true, value: service_type ? service_type.split(',') : [], onChange: (val) => dispatch(setServiceType(val.join(','))), options: typeOptions, placeholder: 'Type: All', displayValue: typeDisplayValue || syncing, leftIcon: '/assets/TypeC.svg', size: 'sm', fullWidth: false, className: 'header-dropdown', usePortal: true }), goal_type === 'am_projection' && (_jsx(_Fragment, { children: hasWaitingApprovalGoal && (_jsx(AppButton, { variant: "contained", onClick: handleApprovedGoalClick, loading: isApproving, sx: {
                                                            height: 40,
                                                            px: 2.5,
                                                            borderRadius: 2,
                                                            textTransform: 'none',
                                                            bgcolor: '#FF008E',
                                                            border: '1px solid #A3005C',
                                                            '&:hover': { bgcolor: '#e0007c' },
                                                            fontSize: '14px',
                                                            fontWeight: 600,
                                                            whiteSpace: 'nowrap',
                                                            boxShadow: 'none'
                                                        }, children: "Approve Goal" })) }))] })] }), syncing ? (_jsx(_Fragment, { children: _jsx(CommonLoader, { className: 'table-loader' }) })) : (_jsxs(_Fragment, { children: [_jsx(Box, { sx: {
                                                position: 'relative',
                                                '& .MuiTableCell-root': {
                                                    whiteSpace: 'nowrap',
                                                },
                                                '& .reusable-table-header-cell-expand, & .reusable-table-cell-expand': {
                                                    width: '24px !important',
                                                    minWidth: '24px !important',
                                                    paddingLeft: '8px !important',
                                                    paddingRight: '0px !important',
                                                },
                                                '& th[data-column-id="clientName"], & td[data-column-id="clientName"]': {
                                                    paddingLeft: '8px !important',
                                                },
                                                '& th .MuiBox-root:has(> .reusable-table-sort-icon)': {
                                                    justifyContent: 'flex-start !important',
                                                },
                                                '& .reusable-table-header-cell-pinned-left, & .reusable-table-cell-pinned-left': {
                                                    paddingLeft: '8px !important',
                                                },
                                                '& .MuiTableContainer-root': {
                                                    msOverflowStyle: 'none',
                                                    scrollbarWidth: 'none',
                                                    '&::-webkit-scrollbar': {
                                                        display: 'none',
                                                    },
                                                }
                                            }, children: _jsx(CommonTable, { columns: columns, rows: rowsWithId, renderCell: renderCell, renderExpandedContent: renderExpandedContent, controlledExpandedRows: expandedRowId ? new Set([expandedRowId]) : new Set(), onExpandToggle: handleExpandToggle, expandTrigger: 'icon', isLoading: loading, enableSelection: goal_type === 'am_projection', isRowSelectable: (row) => row?.status !== 'inactive', selectedRows: selectedRows, onSelectionChange: setSelectedRows, stickyHeader: true, maxHeight: "520px", sortConfig: sortConfig, onSortChange: handleSortChange }) }), !loading && totalEntries > 0 && (_jsx(Box, { className: 'user-list-pagination-wrapper', sx: { paddingInline: '14px' }, children: _jsx(Pagination, { currentPage: page, totalPages: Math.ceil(totalEntries / limit), onPageChange: handlePageChange, maxVisiblePages: 5, totalEntries: totalEntries, itemsPerPage: limit, showEntriesMessage: true }) }))] }))] }) })] }), _jsxs(Dialog, { open: isRefreshModalOpen, onClose: () => setIsRefreshModalOpen(false), maxWidth: "xs", fullWidth: true, sx: {
                    "& .MuiBackdrop-root": {
                        backgroundColor: "rgba(0,0,0,0.5)",
                        backdropFilter: "blur(3px)",
                    },
                    "& .MuiPaper-root": {
                        borderRadius: "8px",
                        p: 1
                    }
                }, children: [_jsxs(DialogTitle, { sx: { m: 0, p: 2 }, children: [_jsx(Typography, { variant: "h6", sx: { fontWeight: 600, fontSize: '24px' }, children: "Sync Goals" }), _jsx(IconButton, { "aria-label": "close", onClick: () => setIsRefreshModalOpen(false), sx: {
                                    position: 'absolute',
                                    right: 8,
                                    top: 8,
                                    color: (theme) => theme.palette.grey[500]
                                }, children: _jsx(CloseIcon, {}) })] }), _jsxs(DialogContent, { sx: { p: 2, pt: 0 }, children: [_jsx(Typography, { sx: { color: '#667085', fontSize: '14px', mb: 1 }, children: "Choose how you'd like to refresh goals from the Goal Sheet." }), _jsxs(Box, { sx: { display: 'flex', gap: 2, mt: 1, alignItems: 'center' }, children: [_jsx(AppButton, { variant: "contained", fullWidth: true, onClick: () => executeSync(false), startIcon: _jsx("img", { src: "/assets/refresh.svg", alt: "", style: { width: 18, height: 18, filter: 'brightness(0) invert(1)', display: 'block' } }), sx: {
                                            height: '44px !important',
                                            minHeight: '44px !important',
                                            maxHeight: '44px !important',
                                            textTransform: 'none',
                                            bgcolor: '#FF008E',
                                            border: '1px solid #FF008E !important',
                                            '&:hover': { bgcolor: '#e0007c', border: '1px solid #e0007c !important' },
                                            fontWeight: 600,
                                            p: '0 !important',
                                            boxSizing: 'border-box !important',
                                            lineHeight: 'normal !important'
                                        }, className: 'all-Font-Sizes', children: "Refresh" }), _jsx(AppButton, { variant: "outlined", fullWidth: true, onClick: () => executeSync(true), startIcon: _jsx("img", { src: "/assets/refresh.svg", alt: "", style: { width: 18, height: 18, opacity: 0.7, display: 'block' } }), sx: {
                                            height: '44px !important',
                                            minHeight: '44px !important',
                                            maxHeight: '44px !important',
                                            textTransform: 'none',
                                            border: '1px solid #D0D5DD !important',
                                            color: '#344054',
                                            '&:hover': { bgcolor: '#F9FAFB', border: '1px solid #D0D5DD !important' },
                                            fontWeight: 600,
                                            p: '0 !important',
                                            boxSizing: 'border-box !important',
                                            lineHeight: 'normal !important'
                                        }, className: 'all-Font-Sizes', children: "Refresh and Notify" })] })] })] }), _jsx(GoalActionDrawer, { open: drawerOpen, onClose: () => setDrawerOpen(false), onSaved: () => dispatch(fetchGoals({ year_month, goal_type, page, limit, am_id: selectedAM, service_type, search_term, sort_by, sort_order })), ...drawerData, year_month: year_month })] }));
};
export default GoalManagementMain;
